import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Form, Input, Button, Card, Table, Dropdown, Menu, Checkbox, Row, Col } from "antd";
import { UnorderedListOutlined, FileTextOutlined,EditOutlined, EyeOutlined } from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import dynamicFormService from "../../services/dynamicForm";

const DynamicFormsIndex = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [visibleDropdown, setVisibleDropdown] = useState(false);
  const [searchForm] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

  // Actions column
  const actionColumn = {
    title: "Actions",
    key: "actions",
    fixed: "right",
    width: 100,
    render: (_, record) => (
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => navigate(`/dynamic-forms/${slug}/edit/${record.id}`)}
        />
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/dynamic-forms/${slug}/details/${record.id}`)}
        />
      </div>
    ),
  };

  // Generate table columns dynamically
  function getTableColumns(includeActions = true) {
    let cols = columns && columns.length > 0
      ? columns.map(c => ({
          title: c.title || c.key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
          key: c.key,
          dataIndex: c.key,
          render: c.render || undefined,
        }))
      : data.length > 0
      ? Object.keys(data[0])
          .filter(key => key !== "id")
          .map(key => ({
            title: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
            key,
            dataIndex: key,
          }))
      : [];

    // cols = cols.filter(c => visibleColumns.includes(c.key));
    cols = cols;

    return includeActions ? [...cols, actionColumn] : cols;
  }

  // Menu for column toggle
  const menu = (
    <Menu>
      {getTableColumns(false).map(col => (
        <Menu.Item key={col.key}>
          <Checkbox
            checked={visibleColumns.includes(col.key)}
            onChange={() => handleColumnToggle(col.key)}
          >
            {col.title}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  // Fetch metadata (columns)
  const fetchFormMetadata = async () => {
    try {
      const meta = await dynamicFormService.getForms();
      const formMeta = meta.find(f => f.slug === slug) || {};
      const cols = formMeta.columns || [];
      setColumns(cols);

      // Initialize visibleColumns
      if (cols.length > 0) {
        setVisibleColumns(cols.map(c => c.key));
      }
    } catch (error) {
      console.error("Failed to fetch dynamic forms metadata:", error);
    }
  };

  // Fetch slug-wise records
  const fetchFormData = async (page = 1, pageSize = 15, filters = {}) => {
    setLoading(true);
    try {
      const response = await dynamicFormService.getFormRecordsBySlug(slug, { ...filters, page, perPage: pageSize });
      const records = response.data || response || [];
      setData(records);

      setPagination({
        current: response.meta?.current_page || page,
        pageSize: response.meta?.per_page || pageSize,
        total: response.meta?.total || records.length,
      });

      // If no metadata columns but data exists, set visibleColumns dynamically
      if ((!columns || columns.length === 0) && records.length > 0 && visibleColumns.length === 0) {
        setVisibleColumns(Object.keys(records[0]).filter(k => k !== "id"));
      }
    } catch (error) {
      console.error("Failed to fetch form data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormMetadata();
    fetchFormData();
    const savedColumns = localStorage.getItem("visibleColumns");
    if (savedColumns) setVisibleColumns(JSON.parse(savedColumns));
  }, [slug]);

  const toggleFormVisibility = () => setFormVisible(!formVisible);

  const handleColumnToggle = key => {
    const updated = visibleColumns.includes(key)
      ? visibleColumns.filter(col => col !== key)
      : [...visibleColumns, key];
    setVisibleColumns(updated);
    localStorage.setItem("visibleColumns", JSON.stringify(updated));
  };

  const handleSearch = values => {
    setFilters(values);
    fetchFormData(1, pagination.pageSize, values);
  };

  const handleTableChange = pag => {
    fetchFormData(pag.current, pag.pageSize, filters);
  };

  return (
    <DefaultLayout>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Header
            icon={<FileTextOutlined />}
            title={`Dynamic Form - ${slug}`}
            right={
              <Link to={`/dynamic-forms/${slug}/add`}>
                <Button type="primary">Add Record</Button>
              </Link>
            }
          />
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Button onClick={toggleFormVisibility}>
          {formVisible ? "Hide Search Form" : "Show Search Form"}
        </Button>

        {formVisible && (
          <Form form={searchForm} layout="vertical" onFinish={handleSearch} style={{ marginTop: 10 }}>
            <Row gutter={[12, 12]}>
              {getTableColumns(false).map(col => (
                <Col key={col.key} lg={6} md={12} sm={24} xs={24}>
                  <Form.Item name={col.key}>
                    <Input placeholder={`Search by ${col.title}`} />
                  </Form.Item>
                </Col>
              ))}
              <Col span={24}>
                <Button type="primary" htmlType="submit" block>
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Card>

      <Row>
        <Col span={24}>
          <Card>
            {/* <Dropdown overlay={menu} trigger={["click"]} visible={visibleDropdown} onVisibleChange={setVisibleDropdown}>
              <Button style={{ marginBottom: 10 }}>
                <UnorderedListOutlined /> Columns
              </Button>
            </Dropdown> */}

            <Table
              columns={getTableColumns()}
              dataSource={data.map(item => ({ ...item, key: item.id }))}
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
              sticky
            />
          </Card>
        </Col>
      </Row>
    </DefaultLayout>
  );
};

export default DynamicFormsIndex;
